import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/cn'

export interface BVNInputHandle {
  /** Returns the raw 11-digit BVN that was entered, or null when invalid. */
  readRaw: () => string | null
  /** Reset to the empty state. */
  reset: () => void
}

interface Props {
  id?: string
  /** Hint text shown below the input. */
  hint?: string
  /** True when an HMAC pepper has been fetched and submission can proceed. */
  pepperReady: boolean
  className?: string
  /** Notifies the parent whether the field currently holds a valid 11-digit BVN. */
  onValidityChange?: (valid: boolean) => void
}

const BVN_LENGTH = 11

/**
 * AF-128 — BVN field with mask-as-you-type semantics.
 *
 * - Numeric-only, 11-digit max.
 * - Once 11 digits are entered (or on blur), the visible value collapses to
 *   `***########` so the BVN doesn't sit in the DOM where a screen-share
 *   (or extension) could read it.
 * - The raw value is held in a ref and surfaced only via `readRaw()` —
 *   the parent never gets it through state, and we never put it in
 *   `aria-label` / `value` after masking.
 * - Submission flow: parent calls `readRaw()`, hashes via `lib/hmac.ts`,
 *   sends the hex digest to the backend. Raw BVNs never cross the wire.
 *
 * Parents control submission disabled-state via `pepperReady` + the
 * `onValidityChange` callback.
 */
export const BVNInput = forwardRef<BVNInputHandle, Props>(function BVNInput(
  { id, hint, pepperReady, className, onValidityChange },
  ref,
) {
  // Two states:
  //   - `displayed`: what the input shows. While typing, this is digits.
  //     After mask, this is `***########`.
  //   - `revealed`: whether the user has explicitly clicked the eye icon
  //     to un-mask (e.g. to verify a paste). Defaults to false.
  const [displayed, setDisplayed] = useState('')
  const [masked, setMasked] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const rawRef = useRef<string>('')

  useImperativeHandle(
    ref,
    () => ({
      readRaw: () => (rawRef.current.length === BVN_LENGTH ? rawRef.current : null),
      reset: () => {
        rawRef.current = ''
        setDisplayed('')
        setMasked(false)
        setRevealed(false)
        onValidityChange?.(false)
      },
    }),
    [onValidityChange],
  )

  const updateValidity = (next: string): void => {
    onValidityChange?.(next.length === BVN_LENGTH)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // While the user is editing, surface the raw digits so they can correct
    // typos. We re-mask on blur or when 11 digits are entered.
    const cleaned = e.target.value.replace(/\D+/g, '').slice(0, BVN_LENGTH)
    rawRef.current = cleaned
    setMasked(false)
    setRevealed(false)
    setDisplayed(cleaned)
    updateValidity(cleaned)
    // Auto-mask once a complete BVN is captured.
    if (cleaned.length === BVN_LENGTH) {
      setTimeout(() => {
        setMasked(true)
        setDisplayed(maskedFor(cleaned))
      }, 200)
    }
  }

  const handleBlur = () => {
    if (rawRef.current.length === BVN_LENGTH) {
      setMasked(true)
      setRevealed(false)
      setDisplayed(maskedFor(rawRef.current))
    }
  }

  const handleFocus = () => {
    // Re-reveal raw value while editing so the user can correct typos.
    if (masked && !revealed) {
      setMasked(false)
      setDisplayed(rawRef.current)
    }
  }

  const toggleReveal = () => {
    if (rawRef.current.length !== BVN_LENGTH) return
    if (revealed) {
      setRevealed(false)
      setMasked(true)
      setDisplayed(maskedFor(rawRef.current))
    } else {
      setRevealed(true)
      setMasked(false)
      setDisplayed(rawRef.current)
    }
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="relative">
        <Input
          id={id}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          aria-label="BVN (11 digits)"
          aria-invalid={
            displayed.length > 0 && rawRef.current.length !== BVN_LENGTH ? true : undefined
          }
          value={displayed}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          maxLength={BVN_LENGTH + 4 /* room for ***</mask> chars */}
          placeholder="11-digit BVN"
          className={cn('pr-10 font-mono tabular-nums', masked && 'tracking-widest')}
        />
        {rawRef.current.length === BVN_LENGTH && (
          <button
            type="button"
            onClick={toggleReveal}
            aria-label={revealed ? 'Hide BVN' : 'Reveal BVN'}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-fg hover:bg-muted hover:text-fg"
          >
            {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
      <p
        className={cn(
          'flex items-center gap-1 text-xs',
          pepperReady ? 'text-muted-fg' : 'text-status-pending',
        )}
      >
        <ShieldCheck size={12} aria-hidden />
        {pepperReady
          ? hint ?? 'BVN is hashed in your browser before submission.'
          : 'Loading session pepper — submission disabled until ready.'}
      </p>
    </div>
  )
})

function maskedFor(bvn: string): string {
  if (bvn.length !== BVN_LENGTH) return bvn
  return `***${bvn.slice(3)}`
}
